import { b as buildPaginationParams, n as normalizeToPaginated } from './chunks/pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic Campaign API Client (NEW - RESTful Design)
 *
 * Updated to use the new microservice-ready campaign controllers:
 * - CampaignsController: Core campaign operations
 * - CampaignTagsController: Tag management
 * - CampaignTokensController: Token unit operations
 * - CampaignTriggersController: Trigger system
 * - CampaignEngagementsController: Business relationships
 * - CampaignClaimsController: Claims processing
 *
 * Uses @explorins/pers-shared DTOs for consistency with backend.
 * All endpoints updated to new RESTful patterns without role revelation.
 */
class CampaignApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }
    // ==========================================
    // CORE CAMPAIGN OPERATIONS (/campaigns)
    // ==========================================
    /**
     * Get campaigns with pagination support
     * Returns paginated response with metadata (total, page, limit, hasMore)
     * Intelligent access: Public gets active only, Business gets own campaigns, Admin gets all
     *
     * @param options - Filter, pagination, and include options
     * @param options.include - Relations to include: 'triggerSources', 'businesses', 'claimCount'
     * @param options.startDate - Filter campaigns starting on or after this date
     * @param options.endDate - Filter campaigns ending on or before this date
     *
     * @example
     * ```typescript
     * // Get campaigns with trigger sources included
     * const campaigns = await api.getCampaigns({ include: ['triggerSources'] });
     * campaigns.data.forEach(c => console.log(c.included?.triggerSources));
     *
     * // Get campaigns with claim count
     * const withCounts = await api.getCampaigns({ include: ['claimCount'] });
     * withCounts.data.forEach(c => console.log(c.claimCount));
     *
     * // Filter by date range
     * const dateFiltered = await api.getCampaigns({
     *   startDate: new Date('2026-05-01'),
     *   endDate: new Date('2026-12-31')
     * });
     * ```
     */
    async getCampaigns(options) {
        const params = [];
        // Always include pagination params to ensure paginated response
        params.push(`page=${options?.page || 1}`);
        params.push(`limit=${options?.limit || 50}`);
        if (options?.active !== undefined)
            params.push(`active=${options.active}`);
        // Support single tag or array of tags (multiple ?tags=a&tags=b)
        if (options?.tags) {
            const tagsArray = Array.isArray(options.tags) ? options.tags : [options.tags];
            tagsArray.forEach(t => params.push(`tags=${encodeURIComponent(t)}`));
        }
        if (options?.businessId)
            params.push(`businessId=${encodeURIComponent(options.businessId)}`);
        if (options?.search)
            params.push(`search=${encodeURIComponent(options.search)}`);
        if (options?.startDate) {
            const dateStr = options.startDate instanceof Date ? options.startDate.toISOString() : options.startDate;
            params.push(`startDate=${encodeURIComponent(dateStr)}`);
        }
        if (options?.endDate) {
            const dateStr = options.endDate instanceof Date ? options.endDate.toISOString() : options.endDate;
            params.push(`endDate=${encodeURIComponent(dateStr)}`);
        }
        if (options?.sortBy)
            params.push(`sortBy=${options.sortBy}`);
        if (options?.sortOrder)
            params.push(`sortOrder=${options.sortOrder}`);
        if (options?.include?.length)
            params.push(`include=${options.include.join(',')}`);
        return this.apiClient.get(`/campaigns?${params.join('&')}`);
    }
    /**
     * PUBLIC: Get campaign by ID
     * NEW: /campaigns/{id}
     *
     * @param id - Campaign UUID
     * @param include - Relations to include: 'triggerSources', 'businesses', 'claimCount'
     *
     * @example
     * ```typescript
     * // Get campaign with all relations
     * const campaign = await api.getCampaignById('123', ['triggerSources', 'businesses']);
     * console.log(campaign.included?.triggerSources);
     * console.log(campaign.included?.businesses);
     *
     * // Get campaign with claim count
     * const withCount = await api.getCampaignById('123', ['claimCount']);
     * console.log(withCount.claimCount);
     * ```
     */
    async getCampaignById(id, include) {
        const params = include?.length ? `?include=${include.join(',')}` : '';
        return this.apiClient.get(`/campaigns/${id}${params}`);
    }
    /**
     * ADMIN: Create campaign
     * NEW: POST /campaigns
     */
    async createCampaign(campaign) {
        return this.apiClient.post('/campaigns', campaign);
    }
    /**
     * ADMIN: Update campaign
     * NEW: PUT /campaigns/{id}
     */
    async updateCampaign(campaignId, campaign) {
        return this.apiClient.put(`/campaigns/${campaignId}`, campaign);
    }
    /**
     * ADMIN: Toggle campaign active status
     * NEW: PUT /campaigns/{id}/status
     */
    async toggleCampaignActive(campaignId) {
        return this.apiClient.put(`/campaigns/${campaignId}/status`, {});
    }
    /**
     * ADMIN: Toggle campaign testnet environment
     * NEW: PUT /campaigns/{id}/environment
     */
    async toggleCampaignTestnet(campaignId) {
        return this.apiClient.put(`/campaigns/${campaignId}/environment`, {});
    }
    /**
     * ADMIN: Delete campaign
     * NEW: DELETE /campaigns/{id}
     */
    async deleteCampaign(campaignId) {
        return this.apiClient.delete(`/campaigns/${campaignId}`);
    }
    // ==========================================
    // TAG MANAGEMENT (/campaign-tags)
    // ==========================================
    /**
     * ADMIN: Get all unique campaign tags
     * NEW: GET /campaign-tags
     */
    async getAllUniqueTags() {
        return this.apiClient.get('/campaigns/tags');
    }
    /**
     * ADMIN: Update campaign tags (replace all)
     * NEW: PUT /campaign-tags/{id}
     */
    async updateCampaignTags(campaignId, tags) {
        return this.apiClient.put(`/campaigns/${campaignId}/tags`, { tags });
    }
    /**
     * ADMIN: Add tags to campaign
     * NEW: POST /campaign-tags/{id}
     */
    async addTagsToCampaign(campaignId, tags) {
        return this.apiClient.post(`/campaigns/${campaignId}/tags`, { tags });
    }
    /**
     * ADMIN: Remove tag from campaign
     * NEW: DELETE /campaign-tags/{id}/{tag}
     */
    async removeTagFromCampaign(campaignId, tag) {
        return this.apiClient.delete(`/campaigns/${campaignId}/tags/${encodeURIComponent(tag)}`);
    }
    // ==========================================
    // TOKEN MANAGEMENT (/campaign-tokens)
    // ==========================================
    /**
     * ADMIN: Create campaign token unit
     * NEW: POST /campaign-tokens/{id}
     */
    async createCampaignTokenUnit(campaignId, campaignTokenUnit) {
        return this.apiClient.post(`/campaigns/${campaignId}/tokens`, campaignTokenUnit);
    }
    /**
     * ADMIN: Update campaign token unit
     * NEW: PUT /campaign-tokens/{id}/{tokenUnitId}
     */
    async updateCampaignTokenUnit(campaignId, tokenUnitId, campaignTokenUnit) {
        return this.apiClient.put(`/campaigns/${campaignId}/tokens/${tokenUnitId}`, campaignTokenUnit);
    }
    /**
     * ADMIN: Delete campaign token unit
     * NEW: DELETE /campaign-tokens/{id}/{tokenUnitId}
     */
    async deleteCampaignTokenUnit(campaignId, campaignTokenUnitId) {
        return this.apiClient.delete(`/campaigns/${campaignId}/tokens/${campaignTokenUnitId}`);
    }
    // ==========================================
    // TRIGGER SYSTEM (/campaign-triggers)
    // ==========================================
    /**
     * PUBLIC: Get campaign triggers catalog with pagination
     * NEW: GET /campaign-triggers
     *
     * @param options - Pagination and sorting options
     * @returns Paginated list of campaign triggers
     *
     * @example
     * ```typescript
     * // Get first page
     * const page1 = await campaignApi.getCampaignTriggers();
     *
     * // Get specific page
     * const page2 = await campaignApi.getCampaignTriggers({ page: 2, limit: 25 });
     *
     * // Access data
     * page1.data.forEach(trigger => console.log(trigger.name));
     * ```
     */
    async getCampaignTriggers(options) {
        const params = buildPaginationParams(options);
        const response = await this.apiClient.get(`/campaign-triggers?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get campaign trigger by ID
     * NEW: GET /campaign-triggers/{id}
     *
     * @param triggerId - Campaign trigger UUID
     * @returns Campaign trigger details
     */
    async getCampaignTriggerById(triggerId) {
        return this.apiClient.get(`/campaign-triggers/${triggerId}`);
    }
    /**
     * ADMIN: Create campaign trigger
     * NEW: POST /campaign-triggers
     */
    async createCampaignTrigger(trigger) {
        return this.apiClient.post(`/campaign-triggers`, trigger);
    }
    /**
     * ADMIN: Update campaign trigger
     * NEW: PUT /campaign-triggers/{id}
     */
    async updateCampaignTrigger(triggerId, trigger) {
        return this.apiClient.put(`/campaign-triggers/${triggerId}`, trigger);
    }
    /**
     * ADMIN: Delete campaign trigger
     * NEW: DELETE /campaign-triggers/{id}
     */
    async deleteCampaignTrigger(triggerId) {
        return this.apiClient.delete(`/campaign-triggers/${triggerId}`);
    }
    /**
     * ADMIN: Assign trigger to campaign
     * NEW: PUT /campaign-triggers/{triggerId}/assign/{campaignId}
     */
    async setCampaignTrigger(campaignId, triggerId) {
        return this.apiClient.put(`/campaign-triggers/${triggerId}/assign/${campaignId}`, {});
    }
    /**
     * ADMIN: Remove trigger from campaign (unassign)
     * NEW: DELETE /campaign-triggers/{triggerId}/assign/{campaignId}
     */
    async removeCampaignTrigger(campaignId, triggerId) {
        return this.apiClient.delete(`/campaign-triggers/${triggerId}/assign/${campaignId}`);
    }
    /**
     * ADMIN: Add/Remove condition to trigger
     * NEW: PUT /campaign-triggers/{triggerId}/conditions/{conditionId}
     */
    async addOrRemoveConditionToTrigger(triggerId, condition) {
        return this.apiClient.put(`/campaign-triggers/${triggerId}/conditions`, condition);
    }
    // ==========================================
    // BUSINESS ENGAGEMENTS (/campaign-engagements)
    // ==========================================
    /**
     * ADMIN: Add business engagement to campaign
     * NEW: POST /campaign-engagements/{id}
     */
    async addBusinessEngagementToCampaign(campaignId, campaignBusinessEngagement) {
        return this.apiClient.post(`/campaigns/${campaignId}/engagements`, campaignBusinessEngagement);
    }
    /**
     * ADMIN: Update campaign business engagement
     * NEW: PUT /campaign-engagements/{id}/{businessEngagementId}
     */
    async updateCampaignBusinessEngagement(campaignId, businessEngagementId, campaignBusinessEngagement) {
        return this.apiClient.put(`/campaigns/${campaignId}/engagements/${businessEngagementId}`, campaignBusinessEngagement);
    }
    /**
     * ADMIN: Delete campaign business engagement
     * NEW: DELETE /campaign-engagements/{id}/{businessEngagementId}
     */
    async deleteCampaignBusinessEngagement(campaignId, businessEngagementId) {
        return this.apiClient.delete(`/campaigns/${campaignId}/engagements/${businessEngagementId}`);
    }
    // ==========================================
    // CLAIMS PROCESSING (/campaign-claims)
    // ==========================================
    /**
     * USER: Claim campaign reward
     */
    async claimCampaign(request) {
        return this.apiClient.post('/campaigns/claims', request);
    }
    /**
     * Unified claims retrieval method with pagination
     * Routes to appropriate endpoint based on filters:
     * - No filters: Admin gets all claims
     * - With userId/campaignId/businessId: Admin gets filtered claims
     *
     * @param filters - Filter and pagination options
     * @returns Paginated list of campaign claims
     *
     * @example
     * ```typescript
     * // Admin: Get all claims
     * const allClaims = await campaignApi.getClaims();
     *
     * // Admin: Get claims for specific user
     * const userClaims = await campaignApi.getClaims({ userId: 'user-123' });
     *
     * // Admin: Get claims for specific campaign (paginated)
     * const page1 = await campaignApi.getClaims({
     *   campaignId: 'campaign-123',
     *   page: 1,
     *   limit: 50
     * });
     *
     * // Admin: Get claims with multiple filters
     * const filtered = await campaignApi.getClaims({
     *   businessId: 'business-456',
     *   page: 2
     * });
     * ```
     */
    async getClaims(filters, include) {
        const params = buildPaginationParams(filters);
        // Add filter parameters
        if (filters?.campaignId)
            params.set('campaignId', filters.campaignId);
        if (filters?.userId)
            params.set('userId', filters.userId);
        if (filters?.businessId)
            params.set('businessId', filters.businessId);
        if (filters?.dateFrom)
            params.set('dateFrom', filters.dateFrom instanceof Date ? filters.dateFrom.toISOString() : filters.dateFrom);
        if (filters?.dateTo)
            params.set('dateTo', filters.dateTo instanceof Date ? filters.dateTo.toISOString() : filters.dateTo);
        // Add include parameter
        if (include && include.length > 0) {
            include.forEach(relation => params.append('include', relation));
        }
        const endpoint = `/campaigns/claims?${params.toString()}`;
        const response = await this.apiClient.get(endpoint);
        return normalizeToPaginated(response);
    }
    /**
     * USER: Get logged-in user's own campaign claims
     * Uses /me endpoint for authenticated user context
     *
     * @param options - Pagination options
     * @param include - Optional relations to include (campaign, user, business)
     * @returns Paginated list of user's claims
     */
    async getClaimsLoggedUser(options, include) {
        const params = buildPaginationParams(options);
        // Add include parameter
        if (include && include.length > 0) {
            include.forEach(relation => params.append('include', relation));
        }
        const response = await this.apiClient.get(`/campaigns/claims/me?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    // ==========================================
    // TRIGGER SOURCE ASSIGNMENT (/campaigns/{id}/trigger-sources)
    // Note: TriggerSource CRUD is in TriggerSourceApi
    // ==========================================
    /**
     * ADMIN: Assign a trigger source to a campaign
     *
     * @param campaignId - Campaign UUID
     * @param triggerSourceId - Trigger source UUID
     * @returns Updated campaign with trigger source assigned
     */
    async assignTriggerSourceToCampaign(campaignId, triggerSourceId) {
        return this.apiClient.put(`/campaigns/${campaignId}/trigger-sources/${triggerSourceId}`, {});
    }
    /**
     * ADMIN: Remove a trigger source from a campaign
     *
     * @param campaignId - Campaign UUID
     * @param triggerSourceId - Trigger source UUID
     * @returns Updated campaign with trigger source removed
     */
    async removeTriggerSourceFromCampaign(campaignId, triggerSourceId) {
        return this.apiClient.delete(`/campaigns/${campaignId}/trigger-sources/${triggerSourceId}`);
    }
    // ==========================================
    // CSV EXPORT
    // ==========================================
    /**
     * ADMIN: Export campaigns to CSV
     *
     * Returns a CSV file with campaign data for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns CSV blob
     */
    async exportCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `/campaigns/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
    /**
     * ADMIN: Export campaign claims to CSV
     *
     * Returns a CSV file with all campaign claim data.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns CSV blob
     */
    async exportClaimsCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `/campaigns/claims/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
}

/**
 * Platform-Agnostic Campaign Service
 *
 * Contains campaign business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 *
 * Focuses only on actual backend capabilities.
 */
class CampaignService {
    constructor(campaignApi) {
        this.campaignApi = campaignApi;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get campaign by ID
     * @param id - Campaign UUID
     * @param include - Relations to include: 'triggerSources', 'businesses'
     */
    async getCampaignById(id, include) {
        return this.campaignApi.getCampaignById(id, include);
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Claim campaign
     */
    async claimCampaign(request) {
        return this.campaignApi.claimCampaign(request);
    }
    /**
     * AUTH: Get claims for logged user with pagination
     * @param options - Pagination options
     * @param include - Optional relations to include (campaign, user, business)
     */
    async getClaimsForLoggedUser(options, include) {
        return this.campaignApi.getClaimsLoggedUser(options, include);
    }
    // ==========================================
    // CAMPAIGN OPERATIONS (Paginated)
    // ==========================================
    /**
     * Get campaigns with pagination support
     * Intelligent access: Public gets active only, Business gets own campaigns, Admin gets all
     * @see CampaignFilterOptions for available filter options
     */
    async getCampaigns(options) {
        return this.campaignApi.getCampaigns(options);
    }
    /**
     * ADMIN: Get campaign triggers with pagination
     */
    async getCampaignTriggers(options) {
        return this.campaignApi.getCampaignTriggers(options);
    }
    /**
     * ADMIN: Get campaign trigger by ID
     */
    async getCampaignTriggerById(triggerId) {
        return this.campaignApi.getCampaignTriggerById(triggerId);
    }
    /**
     * ADMIN: Create campaign trigger
     */
    async createCampaignTrigger(data) {
        return this.campaignApi.createCampaignTrigger(data);
    }
    /**
     * ADMIN: Update campaign trigger
     */
    async updateCampaignTrigger(triggerId, data) {
        return this.campaignApi.updateCampaignTrigger(triggerId, data);
    }
    /**
     * ADMIN: Delete campaign trigger
     */
    async deleteCampaignTrigger(triggerId) {
        return this.campaignApi.deleteCampaignTrigger(triggerId);
    }
    /**
     * ADMIN: Toggle campaign active status
     */
    async toggleCampaignActive(campaignId) {
        return this.campaignApi.toggleCampaignActive(campaignId);
    }
    /**
     * ADMIN: Toggle campaign testnet environment
     */
    async toggleCampaignTestnet(campaignId) {
        return this.campaignApi.toggleCampaignTestnet(campaignId);
    }
    /**
     * ADMIN: Create campaign
     */
    async createCampaign(campaign) {
        return this.campaignApi.createCampaign(campaign);
    }
    /**
     * ADMIN: Set campaign trigger
     */
    async setCampaignTrigger(campaignId, triggerId) {
        return this.campaignApi.setCampaignTrigger(campaignId, triggerId);
    }
    /**
     * ADMIN: Remove trigger from campaign (unassign)
     */
    async removeCampaignTrigger(campaignId, triggerId) {
        return this.campaignApi.removeCampaignTrigger(campaignId, triggerId);
    }
    /**
     * ADMIN: Update campaign
     */
    async updateCampaign(campaignId, campaign) {
        return this.campaignApi.updateCampaign(campaignId, campaign);
    }
    /**
     * ADMIN: Create campaign token unit
     */
    async createCampaignTokenUnit(campaignId, campaignTokenUnit) {
        return this.campaignApi.createCampaignTokenUnit(campaignId, campaignTokenUnit);
    }
    /**
     * ADMIN: Delete campaign token unit
     */
    async deleteCampaignTokenUnit(campaignId, campaignTokenUnitId) {
        return this.campaignApi.deleteCampaignTokenUnit(campaignId, campaignTokenUnitId);
    }
    /**
     * ADMIN: Add business engagement to campaign
     */
    async addBusinessEngagementToCampaign(campaignId, campaignBusinessEngagement) {
        return this.campaignApi.addBusinessEngagementToCampaign(campaignId, campaignBusinessEngagement);
    }
    /**
     * ADMIN: Update campaign business engagement
     */
    async updateCampaignBusinessEngagement(campaignId, businessEngagementId, campaignBusinessEngagement) {
        return this.campaignApi.updateCampaignBusinessEngagement(campaignId, businessEngagementId, campaignBusinessEngagement);
    }
    async deleteCampaignBusinessEngagement(campaignId, businessEngagementId) {
        return this.campaignApi.deleteCampaignBusinessEngagement(campaignId, businessEngagementId);
    }
    /**
     * ADMIN: Get campaign claims with optional filters and pagination
     * @param filters - Filter and pagination options including date range
     * @param include - Optional relations to include (campaign, user, business)
     */
    async getCampaignClaims(filters, include) {
        return this.campaignApi.getClaims(filters, include);
    }
    /**
     * ADMIN: Get campaign claims by user ID with pagination
     * @param userId - User ID to filter by
     * @param options - Pagination options
     * @param include - Optional relations to include (campaign, user, business)
     */
    async getCampaignClaimsByUserId(userId, options, include) {
        return this.campaignApi.getClaims({ userId, ...options }, include);
    }
    /**
     * ADMIN: Get campaign claims by business ID with pagination
     * @param businessId - Business ID to filter by
     * @param options - Pagination options
     * @param include - Optional relations to include (campaign, user, business)
     */
    async getCampaignClaimsByBusinessId(businessId, options, include) {
        return this.campaignApi.getClaims({ businessId, ...options }, include);
    }
    // ==========================================
    // TRIGGER SOURCE ASSIGNMENT
    // Note: TriggerSource CRUD is in TriggerSourceService
    // ==========================================
    /**
     * ADMIN: Assign a trigger source to a campaign
     */
    async assignTriggerSourceToCampaign(campaignId, triggerSourceId) {
        return this.campaignApi.assignTriggerSourceToCampaign(campaignId, triggerSourceId);
    }
    /**
     * ADMIN: Remove a trigger source from a campaign
     */
    async removeTriggerSourceFromCampaign(campaignId, triggerSourceId) {
        return this.campaignApi.removeTriggerSourceFromCampaign(campaignId, triggerSourceId);
    }
}

export { CampaignApi, CampaignService };
//# sourceMappingURL=campaign.js.map
